// src/components/CreateTaskModal/CreateTaskModal.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";
import { Formik } from "formik";
import * as Yup from "yup";
import axiosInstace from "../../axiosInstace";
import "./CreateTaskModal.scss";
import DeleteButton from "../DeleteButton/DeleteButton";

// UI components (paths - adjust if your project uses different folders)
import InputField from "../InputComponent/InputField";
import ButtonWithIcon from "../ButtonwithIcon/ButtonWithIcon";
import AddButton from "../AddButton";
import RemoveButton from "../../components/RemoveButton/RemoveButton";
import { createPortal } from "react-dom";
import { resolveAssigneeId } from "../AssignMemberSelect/AssignMemberSelect";
import {
  normalizeRole,
  defaultTodoAssignee,
  canAssignTask,
  canAssignTodo,
  canSeeTaskAssignUI,
  isForbiddenAssignment, // ✅ ADD THIS
} from "../../config/roles";

export default function CreateTaskModal({
  projectId,
  onClose,
  onCreated,
  creatorEmpId = null, // project creator; NOT the logged-in user
  currentEmpId = null, // logged-in user
  stages = [],
}) {
  const BACKEND_BASE =
    process.env.REACT_APP_BACKEND_URL || "http://localhost:8082";

  // --- state ---
  const [projectMembers, setProjectMembers] = useState([]);
  const [loadingMembers, setLoadingMembers] = useState(true);
  const [todos, setTodos] = useState([]);
  const titleRef = useRef(null);
  const todoTitleRef = useRef(null);

  const [selectedTodoId, setSelectedTodoId] = useState(null);

  // Unified assign modal target: { mode: "task" } or { mode: "todo", todoId }
  const [assigningTarget, setAssigningTarget] = useState(null);

  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);

  // task-level assigned emp id
  const [taskAssignedEmpId, setTaskAssignedEmpId] = useState(null);
  // show creator by default if no explicit taskAssignedEmpId selected

  // sidebar & UI
  const stepOrder = ["details", "todos"];
  const [activeStep, setActiveStep] = useState("details");
  const menuRef = useRef(null);
  const itemsRef = useRef({});
  const highlightRef = useRef(null);

  // --- who is logged in ---
  // NOTE: `creatorEmpId` is the *project creator*. It used to be treated as the
  // logged-in user here, which made every user appear (and be treated) as the
  // Creator. Identity now comes from the logged-in user; creatorEmpId is only
  // a last-resort fallback for callers that don't pass currentEmpId yet.
  const loggedInEmpId = (() => {
    if (currentEmpId) return currentEmpId;
    try {
      const stored = JSON.parse(sessionStorage.getItem("user") || "null");
      if (stored?.empId) return stored.empId;
    } catch {
      /* ignore */
    }
    return creatorEmpId ?? null;
  })();

  // find current user member object in the projectMembers list
  const currentUserMember = projectMembers.find(
    (m) => String(m.empId) === String(loggedInEmpId),
  );

  const currentUserRole = normalizeRole(
    currentUserMember?.roleType ?? currentUserMember?.roleId ?? null,
  );
  const displayedAssignedEmpId =
    taskAssignedEmpId !== null && taskAssignedEmpId !== undefined
      ? Number(taskAssignedEmpId)
      : Number(loggedInEmpId ?? null);

  // --- fetch project members ---
  useEffect(() => {
    let mounted = true;
    async function fetchMembers() {
      setLoadingMembers(true);
      try {
        if (!projectId) {
          if (mounted) setProjectMembers([]);
          return;
        }
        const res = await axiosInstace.get(
          `${BACKEND_BASE}/api/emp/projects/projectmemberaccess/${projectId}/allmembers`,
        );
        const list = Array.isArray(res?.data?.data) ? res.data.data : [];
        // normalize shape
        const normalized = list.map((m) => ({
          accessId: m.accessId ?? null,
          projectId: Number(m.projectId ?? projectId),
          empId: Number(m.empId ?? m.employeeId ?? m.id),
          empName: m.empName ?? m.employeeName ?? m.name ?? `#${m.empId}`,
          roleId: Number(m.roleId ?? 0),
          roleType: m.roleType ?? "",
        }));
        if (mounted) setProjectMembers(normalized);
      } catch (err) {
        console.error("[CreateTaskModal] members fetch error:", err);
        if (mounted) setProjectMembers([]);
      } finally {
        if (mounted) setLoadingMembers(false);
      }
    }
    fetchMembers();
    return () => (mounted = false);
  }, [projectId, BACKEND_BASE]);

  // UI highlight for sidebar
  useEffect(() => {
    if (!menuRef.current || !highlightRef.current) return;
    const el = itemsRef.current[activeStep];
    if (!el) return;
    const parentTop = menuRef.current.getBoundingClientRect().top;
    const { top, height } = el.getBoundingClientRect();
    const y = top - parentTop;
    highlightRef.current.style.transform = `translateY(${y}px)`;
    highlightRef.current.style.height = `${height}px`;
  }, [activeStep, todos.length]);

  // min start date (today)
  const minStartDate = useMemo(() => {
    const d = new Date();
    return d.toISOString().split("T")[0]; // YYYY-MM-DD for input[type=date]
  }, []);

  // Esc key to close modal
  useEffect(() => {
    const handleKey = (e) => {
      if (e.key === "Escape" || e.key === "Esc") {
        onClose && onClose();
      }
    };
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [onClose]);

  // utility id generator
  const makeId = () => Math.random().toString(36).slice(2, 9);

  const addTodo = (payload = null) => {
    const container = document.querySelector(".todos-wrap4567");
    const prevScrollTop = container?.scrollTop ?? 0;

    const newTodo = {
      id: makeId(),
      toDoTitle: payload?.toDoTitle ?? "",
      remark: payload?.remark ?? "",
      priority: payload?.priority ?? "MEDIUM",
      stagePositionId: payload?.stagePositionId ?? firstStageId ?? null,
      assignedEmpId:
        payload?.assignedEmpId ??
        defaultTodoAssignee(currentUserRole, loggedInEmpId),
      toDoList: payload?.toDoList ?? [
        { id: makeId(), toDoListTitle: "", completion: false },
      ],
    };

    // 🔥 THIS IS THE IMPORTANT PART
    setTodos((prev) => [newTodo, ...prev]);

    setSelectedTodoId(newTodo.id);

    // keep viewport stable
    requestAnimationFrame(() => {
      if (container) {
        container.scrollTop = prevScrollTop + 70;
      }
    });

    return newTodo.id;
  };

  const updateTodo = (id, key, value) =>
    setTodos((prev) =>
      prev.map((t) => (t.id === id ? { ...t, [key]: value } : t)),
    );

  const removeTodo = (id) => {
    setTodos((prev) => prev.filter((t) => t.id !== id));
    if (selectedTodoId === id) setSelectedTodoId(null);
  };
  // --- compute firstStageId from stages prop ---
  const firstStageId = useMemo(() => {
    try {
      if (!Array.isArray(stages) || stages.length === 0) return null;
      const firstStage = stages[0];
      const posList =
        firstStage?.stagePositionResponseDTOList ??
        firstStage?.stagePositionResponseDTOs ??
        firstStage?.stagePositionList ??
        [];
      if (!Array.isArray(posList) || posList.length === 0) return null;
      return posList[0]?.stagePositionId ?? posList[0]?.id ?? null;
    } catch (e) {
      return null;
    }
  }, [stages]);

  const addTodoListItem = (todoId, title = "") =>
    setTodos((prev) =>
      prev.map((t) =>
        t.id === todoId
          ? {
              ...t,
              toDoList: [
                ...(t.toDoList || []),
                { id: makeId(), toDoListTitle: title || "", completion: false },
              ],
            }
          : t,
      ),
    );

  const removeTodoListItem = (todoId, itemId) =>
    setTodos((prev) =>
      prev.map((t) =>
        t.id === todoId
          ? {
              ...t,
              toDoList: (t.toDoList || []).filter((i) => i.id !== itemId),
            }
          : t,
      ),
    );

  // --- Assignment handlers (unified) ---
  const openAssignModal = (target) => {
    // target = { mode: "task" } OR { mode: "todo", todoId }
    setAssigningTarget(target);
  };

  const assignMember = (member) => {
    if (!assigningTarget) return;
    if (assigningTarget.mode === "task") {
      setTaskAssignedEmpId(Number(member.empId));
    } else {
      const todoId = assigningTarget.todoId;
      setTodos((prev) => {
        const next = prev.map((t) =>
          t.id === todoId ? { ...t, assignedEmpId: Number(member.empId) } : t,
        );

        return next;
      });
    }
    // close AFTER state update trigger
    setAssigningTarget(null);
  };

  // helper lookups
  const getMemberByEmpId = (empId) =>
    projectMembers.find((m) => String(m.empId) === String(empId)) ?? null;
  const getAccessIdForEmpId = (empId) =>
    getMemberByEmpId(empId)?.accessId ?? null;

  // build todoRequestDTOList
  // Replace existing buildTodoRequestDTOList_enforced with this
  const buildTodoRequestDTOList_enforced = () => {
    return (
      (todos || [])
        .map((t) => {
          // Creator/Approver: chosen project member (subject to
          // isForbiddenAssignment). Everyone else, or nobody chosen: the
          // logged-in user.
          const allowedAssigneeEmpId = resolveAssigneeId({
            selectedEmpId: t.assignedEmpId,
            currentEmpId: loggedInEmpId,
            members: projectMembers,
            currentUserRole,
            scope: "todo",
          });

          // normalize & filter checklist items (remove empty titles)
          const cleanedChecklist = (t.toDoList || [])
            .map((li) => ({
              toDoListTitle: (li.toDoListTitle ?? "").toString(),
              completion: Boolean(li.completion),
            }))
            .filter((li) => (li.toDoListTitle || "").trim() !== "");

          // base todo DTO
          const isTodoSelfAssigned =
            Number(allowedAssigneeEmpId) === Number(loggedInEmpId);

          const todoDtoBase = {
            toDoTitle: t.toDoTitle ?? "",
            remark: t.remark ?? null,
            stagePositionId: t.stagePositionId ?? firstStageId ?? null,
            priority: t.priority || "MEDIUM",

            // ✅ only send accessId if assigned to someone else
            ...(!isTodoSelfAssigned && allowedAssigneeEmpId
              ? { accessId: getAccessIdForEmpId(allowedAssigneeEmpId) }
              : {}),
          };

          // conditionally attach checklist only when non-empty
          if (cleanedChecklist.length > 0) {
            todoDtoBase.toDoListRequestDTOList = cleanedChecklist;
          }

          return todoDtoBase;
        })
        // filter out todos that are effectively empty: no title, no remark, no checklist
        .filter(
          (t) =>
            (t.toDoTitle || "").toString().trim() !== "" ||
            (t.remark || "").toString().trim() !== "" ||
            (Array.isArray(t.toDoListRequestDTOList) &&
              t.toDoListRequestDTOList.length > 0),
        )
    );
  };

  const initialValues = {
    projectId: projectId || "",
    taskTitle: "",
    description: "",
    remark: "",
    schedulingMode: "DAYS", // default to DAYS
    startDate: "", // will auto-set to today when needed
    endDate: "",
    durationDays: 2, // default duration
    priority: "MEDIUM",
  };

  // validation schema
  const validationSchema = Yup.object().shape({
    projectId: Yup.mixed().test("required", "Project id is required.", (v) => {
      const n = Number(v);
      return Number.isFinite(n) && n > 0;
    }),
    taskTitle: Yup.string().trim().required("Task title is required."),
    schedulingMode: Yup.string().oneOf(["DATES", "DAYS"]).required(),
    startDate: Yup.string().when("schedulingMode", (mode, schema) => {
      if (mode === "DATES") {
        return schema
          .required("Start date is required.")
          .test(
            "min-start",
            `Start date must be on or after ${minStartDate}`,
            (value) => !value || new Date(value) >= new Date(minStartDate),
          );
      }
      return schema.strip();
    }),
    endDate: Yup.string().when("schedulingMode", (mode, schema) => {
      if (mode === "DATES") {
        return schema
          .required("End date is required.")
          .test(
            "end-after-start",
            "End date must be same or after start date.",
            function (value) {
              const startDate = this.parent.startDate;
              if (!startDate || !value) return false;
              return new Date(value) >= new Date(startDate);
            },
          );
      }
      return schema.strip();
    }),
    durationDays: Yup.number().when("schedulingMode", (mode, schema) => {
      if (mode === "DAYS") {
        return schema.required("Duration is required.").min(1, "Minimum 1 day");
      }
      return schema.strip();
    }),
    priority: Yup.string().oneOf(["LOW", "MEDIUM", "HIGH"]).required(),
  });

  // submit
  const submitTask = async (values, { resetForm }) => {
    setError("");
    setMessage("");
    setSubmitting(true);

    const toYMD = (date) => {
      const d = date instanceof Date ? date : new Date(date);
      if (Number.isNaN(d.getTime())) return null;
      const y = d.getFullYear();
      const m = String(d.getMonth() + 1).padStart(2, "0");
      const day = String(d.getDate()).padStart(2, "0");
      return `${y}-${m}-${day}`;
    };

    try {
      // basic validation
      if (!values.projectId) throw new Error("Project is required");
      if (!values.taskTitle || !values.taskTitle.trim())
        throw new Error("Task title is required");

      const modeForm = values.schedulingMode; // "DATES" or "DAYS" in your form
      const schedulingModeForServer = modeForm === "DATES" ? "DATE" : "DAY";

      // ensure startDate exists for DAYS mode (default to today if missing)
      let startDateVal = values.startDate ? String(values.startDate) : "";
      if (modeForm === "DAYS" && !startDateVal) {
        startDateVal = toYMD(new Date()); // default startDate to today
      }

      if (!startDateVal) {
        throw new Error(
          "Start date is required for the selected scheduling mode",
        );
      }
      const startDateFormatted = toYMD(startDateVal);
      if (!startDateFormatted) throw new Error("Invalid start date");

      // Picked member (allowed roles only, must be on this project, and not
      // forbidden by isForbiddenAssignment); otherwise the logged-in user owns
      // the stage.
      const effectiveTaskAssignedEmpId = resolveAssigneeId({
        selectedEmpId: taskAssignedEmpId,
        currentEmpId: loggedInEmpId,
        members: projectMembers,
        currentUserRole,
        scope: "task",
      });

      // Build payload differently per mode
      // Build base payload (do NOT include todos yet)
      const isSelfAssigned =
        Number(effectiveTaskAssignedEmpId) === Number(loggedInEmpId);

      const base = {
        taskTitle: values.taskTitle.trim(),
        description: values.description?.trim() || null,
        remark: values.remark?.trim() || null,
        schedulingMode: schedulingModeForServer,
        startDate: startDateFormatted,
        projectId: Number(values.projectId),
        stagePositionId: firstStageId ?? null,
        priority: values.priority,

        // ✅ only send accessId if NOT self-assigned
        ...(!isSelfAssigned && effectiveTaskAssignedEmpId
          ? { accessId: getAccessIdForEmpId(effectiveTaskAssignedEmpId) }
          : {}),
      };

      // build filtered todos
      const todoRequestDTOList = buildTodoRequestDTOList_enforced();

      // attach todos only if there's at least one non-empty todo
      let payload;
      if (schedulingModeForServer === "DATE") {
        if (!values.endDate)
          throw new Error("End date is required for DATE mode");
        const endDateFormatted = toYMD(values.endDate);
        if (!endDateFormatted) throw new Error("Invalid end date");
        if (
          new Date(endDateFormatted).getTime() <
          new Date(startDateFormatted).getTime()
        ) {
          throw new Error("End date cannot be earlier than start date");
        }

        payload = {
          ...base,
          endDate: endDateFormatted,
          durationDays: null,
          ...(Array.isArray(todoRequestDTOList) && todoRequestDTOList.length
            ? { todoRequestDTOList }
            : {}),
        };
      } else {
        // DAY mode
        if (
          values.durationDays === undefined ||
          values.durationDays === null ||
          values.durationDays === ""
        ) {
          throw new Error("Duration days is required for DAY mode");
        }
        const durationVal = Number(values.durationDays);
        if (!Number.isFinite(durationVal) || durationVal < 1)
          throw new Error("Duration days must be >= 1");

        payload = {
          ...base,
          durationDays: durationVal,
          ...(Array.isArray(todoRequestDTOList) && todoRequestDTOList.length
            ? { todoRequestDTOList }
            : {}),
        };
      }

      const res = await axiosInstace.post(
        `${BACKEND_BASE}/api/emp/tasks/create`,
        [payload],
        {
          headers: { "Content-Type": "application/json" },
        },
      );

      // normalize response to guarantee stagePositionId is set
      const createdFromServer = res?.data?.data ?? null;

      const created = {
        ...(createdFromServer || {}),
        stagePositionId:
          createdFromServer?.stagePositionId ??
          payload.stagePositionId ??
          firstStageId ??
          1, // ✅ fallback to stage ID 1 if nothing else
      };

      setMessage(res?.data?.message || "Task created successfully.");
      onCreated && onCreated(created);
      resetForm();

      setTodos([]);
      setSelectedTodoId(null);
      setTaskAssignedEmpId(null);

      try {
        console.groupCollapsed(
          "[CreateTaskModal] /api/emp/tasks/create response",
        );

        console.groupEnd();
      } catch (e) {}

      setTimeout(() => onClose && onClose(), 500);
    } catch (err) {
      console.error("[CreateTaskModal] create error:", err);
      if (err?.response) {
        console.groupCollapsed("[CreateTaskModal] create error details");

        console.groupEnd();
      }
      setError(
        err?.response?.data?.message ||
          err.message ||
          "Failed to create stage.",
      );
    } finally {
      setSubmitting(false);
    }
  };
  // Small Assign modal (unified for task & todo)
  const AssignModal = ({ visible, target, onClose }) => {
    if (!visible || !target) return null;

    const isTaskMode = target?.mode === "task";
    const todoId = target?.todoId ?? null;
    const selectedTodo = todos.find((t) => t.id === todoId) || null;
    const canAssignHere = isTaskMode
      ? canAssignTask(currentUserRole)
      : canAssignTodo(currentUserRole);

    // who is already assigned in this mode?
    const assignedEmpIdInScope = isTaskMode
      ? (taskAssignedEmpId ?? Number(loggedInEmpId ?? null))
      : (selectedTodo?.assignedEmpId ?? Number(loggedInEmpId ?? null));

    return (
      <div className="assign-modal-overlay4567" style={assignModalOverlayStyle}>
        <div className="assign-modal4567" style={assignModalStyle}>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: 10,
            }}
          >
            <h4 style={{ margin: 0 }}>
              {isTaskMode ? "Assign member to Stage" : "Assign member to Task"}
            </h4>
            <button
              onClick={() => {
                onClose && onClose();
              }}
              style={closeBtnStyle}
              aria-label="Close"
            >
              &times;
            </button>
          </div>

          {!isTaskMode && (
            <div style={{ marginBottom: 8 }}>
              ToDo:{" "}
              <strong>
                {selectedTodo
                  ? selectedTodo.toDoTitle ||
                    `#${todos.indexOf(selectedTodo) + 1}`
                  : "—"}
              </strong>
            </div>
          )}

          <div
            style={{
              overflowY: "auto",
              overflowX: "auto",
              maxHeight: 340,
              WebkitOverflowScrolling: "touch",
            }}
          >
            <table
              style={{
                width: "100%",
                minWidth: 360,
                borderCollapse: "collapse",
              }}
            >
              <thead>
                <tr>
                  <th
                    style={{
                      textAlign: "left",
                      padding: 8,
                      borderBottom: "1px solid #eee",
                      width: "40%",
                    }}
                  >
                    Name
                  </th>
                  <th
                    style={{
                      textAlign: "left",
                      padding: 8,
                      borderBottom: "1px solid #eee",
                      width: "20%",
                    }}
                  >
                    Role
                  </th>
                  <th
                    style={{
                      textAlign: "left",
                      padding: 8,
                      borderBottom: "1px solid #eee",
                    }}
                  >
                    Action
                  </th>
                </tr>
              </thead>
              <tbody>
                {loadingMembers ? (
                  <tr>
                    <td colSpan={3} style={{ padding: 12, color: "#666" }}>
                      Loading members…
                    </td>
                  </tr>
                ) : projectMembers.length === 0 ? (
                  <tr>
                    <td colSpan={3} style={{ padding: 12, color: "#666" }}>
                      No project members.
                    </td>
                  </tr>
                ) : (
                  projectMembers.map((m) => {
                    const memberRole = normalizeRole(m.roleType ?? m.roleId);
                    const forbidden = isForbiddenAssignment(
                      currentUserRole,
                      memberRole,
                      isTaskMode ? "task" : "todo",
                    );

                    const isAssigned =
                      Number(assignedEmpIdInScope) === Number(m.empId);
                    const someoneElseAssigned =
                      assignedEmpIdInScope &&
                      Number(assignedEmpIdInScope) !== Number(m.empId) &&
                      !canAssignHere; // only lock if user cannot assign

                    return (
                      <tr
                        key={`${m.empId}-${m.accessId || "na"}`}
                        style={{ borderBottom: "1px solid #fafafa" }}
                      >
                        <td style={{ padding: 8 }}>{m.empName}</td>
                        <td style={{ padding: 8 }}>{m.roleType || m.roleId}</td>

                        {/* Action cell */}
                        <td style={{ padding: 8 }}>
                          {/* If current user cannot assign at all */}
                          {!canAssignHere ? (
                            <span style={{ fontSize: 12, color: "#999" }}>
                              No permission
                            </span>
                          ) : isAssigned ? (
                            <RemoveButton
                              onClick={() => {
                                if (isTaskMode) {
                                  setTaskAssignedEmpId(null);
                                } else {
                                  setTodos((prev) =>
                                    prev.map((t) =>
                                      t.id === todoId &&
                                      Number(t.assignedEmpId) ===
                                        Number(m.empId)
                                        ? { ...t, assignedEmpId: null }
                                        : t,
                                    ),
                                  );
                                }
                                onClose && onClose();
                              }}
                              title={
                                isTaskMode
                                  ? "Remove from Task"
                                  : "Remove from ToDo"
                              }
                              size={26}
                            />
                          ) : // inside projectMembers.map((m) => { ... })
                          forbidden ? (
                            <div style={{ fontSize: 12, color: "#999" }}>
                              Cannot assign to{" "}
                              {String(m.roleType || "this role").toLowerCase()}.
                            </div>
                          ) : (
                            <AddButton
                              onClick={() => {
                                assignMember(m);
                              }}
                              title={
                                isTaskMode
                                  ? "Assign to Task"
                                  : "Assign to this ToDo"
                              }
                              size={26}
                              label="Add"
                              disabled={
                                Boolean(someoneElseAssigned) ||
                                Boolean(forbidden)
                              }
                            />
                          )}
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          <div
            style={{
              marginTop: 12,
              display: "flex",
              justifyContent: "flex-end",
              gap: 8,
            }}
          >
            <button
              onClick={() => {
                onClose && onClose();
              }}
              className="btn-cancel4567"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    );
  };

  useEffect(() => {
    if (titleRef.current) {
      titleRef.current.focus();
    }
  }, []); // runs once when modal appears

  useEffect(() => {
    if (activeStep === "todos") {
      setTimeout(() => {
        if (todoTitleRef.current) todoTitleRef.current.focus();
      }, 50);
    }
  }, [activeStep]);

  // Replace the whole useEffect that listens to [selectedTodoId, activeStep, todos.length]
  useEffect(() => {
    if (!selectedTodoId || activeStep !== "todos") return;

    const t = setTimeout(() => {
      const card = document.getElementById(`todo-card-${selectedTodoId}`);
      if (!card) return;

      // prefer nearest scrollable parent
      const findScrollableAncestor = (el) => {
        let node = el.parentElement;
        while (node) {
          const style = getComputedStyle(node);
          const overflowY = style.overflowY;
          const scrollable =
            (overflowY === "auto" || overflowY === "scroll") &&
            node.scrollHeight > node.clientHeight;
          if (scrollable) return node;
          node = node.parentElement;
        }
        return (
          document.querySelector(".todos-wrap4567") ||
          document.scrollingElement ||
          document.documentElement
        );
      };

      const container = findScrollableAncestor(card);

      // Try element.scrollIntoView first — handles last-item clipping well.
      try {
        card.scrollIntoView({
          behavior: "smooth",
          block: "center",
          inline: "nearest",
        });
      } catch (err) {
        // fallback: attempt manual container scroll
        if (
          container &&
          container !== document.documentElement &&
          container !== document.body
        ) {
          const top = card.offsetTop - container.clientHeight / 3;
          container.scrollTo({ top, behavior: "smooth" });
        } else {
          const rect = card.getBoundingClientRect();
          const top = rect.top + window.pageYOffset - 120;
          window.scrollTo({ top, behavior: "smooth" });
        }
      }

      // then focus the input inside the card
      setTimeout(() => {
        const input = document.getElementById(`todo-${selectedTodoId}-title`);
        if (input && typeof input.focus === "function") {
          input.focus();
          try {
            input.select && input.select();
          } catch (e) {}
        }
      }, 120);
    }, 30);

    return () => clearTimeout(t);
  }, [selectedTodoId, activeStep, todos.length]);

  // Ensure one todo is present by default when user switches to todos
  useEffect(() => {
    if (activeStep === "todos" && todos.length === 0) {
      addTodo();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeStep]);

  // Render
  return (
    <div className="cp-modal-overlay4567">
      <div className="cp-modal4567" role="dialog" aria-modal="true">
        <button
          type="button"
          className="cp-modal-close4567"
          onClick={() => onClose && onClose()}
          aria-label="Close modal"
        >
          ×
        </button>

        <aside className="cp-sidebar4567">
          <h2>Create Stage</h2>
          <ul ref={menuRef} className="sidebar-menu4567">
            {stepOrder.map((s) => (
              <li
                key={s}
                ref={(el) => (itemsRef.current[s] = el)}
                className={activeStep === s ? "active4567" : ""}
                onClick={() => setActiveStep(s)}
              >
                {s === "details" ? "Stage Details" : "Tasks"}
              </li>
            ))}
            <div className="highlight4567" ref={highlightRef} />
          </ul>
          <div className="sidebar-note4567">
            Project: {projectId || "(none)"}
          </div>
          <div style={{ marginTop: 8, fontSize: 13, color: "#666" }}>
            {/* You are:{" "}
          <strong>
            {currentUserMember ? currentUserMember.empName : "Unknown"}
          </strong> */}
            <br />
            Role:{" "}
            <strong>
              {currentUserMember
                ? String(currentUserMember.roleType || currentUserMember.roleId)
                    .replace(/_/g, " ")
                    .toLowerCase()
                    .replace(/^\w/, (c) => c.toUpperCase())
                : "—"}
            </strong>
          </div>
        </aside>

        <main className="cp-content4567">
          <div className="cp-form-scroll4567">
            <Formik
              initialValues={{ ...initialValues, projectId: projectId || "" }}
              enableReinitialize
              validationSchema={validationSchema}
              onSubmit={submitTask}
            >
              {({
                values,
                errors,
                touched,
                isSubmitting,
                setFieldValue,
                handleSubmit,
              }) => (
                <form onSubmit={handleSubmit}>
                  {/* Details */}
                  <section
                    className={`section4567 ${
                      activeStep === "details" ? "active-section4567" : ""
                    }`}
                    aria-hidden={activeStep !== "details"}
                    style={{
                      display: activeStep === "details" ? "block" : "none",
                    }}
                  >
                    {/* --- Line 1: Assign label + avatar chip + assign button --- */}
                    <div
                      className="details-line4567 details-line-14567"
                      style={{
                        marginTop: 8,
                        display: "flex",
                        alignItems: "center",
                        gap: 12,
                      }}
                    >
                      <strong style={{ flex: "0 0 auto" }}>Assign Stage</strong>

                      {/* avatar chip / assigned */}
                      <div style={{ flex: "0 0 auto" }}>
                        {displayedAssignedEmpId ? (
                          <div
                            className="avatar-chip4567"
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: 8,
                              background: "#f5f7fb",
                              padding: "6px 10px",
                              borderRadius: 20,
                            }}
                          >
                            <div
                              style={{
                                width: 28,
                                height: 28,
                                borderRadius: 999,
                                background: "#e6eefc",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                fontWeight: 700,
                              }}
                            >
                              {getMemberByEmpId(
                                displayedAssignedEmpId,
                              )?.empName?.charAt(0) ||
                                String(displayedAssignedEmpId).charAt(0) ||
                                "?"}
                            </div>
                            <div style={{ fontSize: 13 }}>
                              {getMemberByEmpId(displayedAssignedEmpId)
                                ?.empName ||
                                (displayedAssignedEmpId ===
                                Number(loggedInEmpId)
                                  ? "You"
                                  : `#${displayedAssignedEmpId}`)}
                            </div>
                          </div>
                        ) : (
                          <div style={{ color: "#999", fontSize: 12 }}>
                            Unassigned
                          </div>
                        )}
                      </div>

                      {/* Assign / Reassign button sits at the right of this line */}
                      <div
                        className="cp-field-assignbtn"
                        style={{ flex: "300px" }}
                      >
                        {canSeeTaskAssignUI(currentUserRole) && (
                          <ButtonWithIcon
                            text={
                              taskAssignedEmpId
                                ? "Reassign Stage"
                                : "Assign Stage"
                            }
                            icon="mdi:account-plus"
                            iconPosition="right"
                            width={190}
                            type="button"
                            onClick={() => openAssignModal({ mode: "task" })}
                          />
                        )}
                      </div>
                    </div>

                    {/* --- Line 2: Task Title, Priority, Scheduling Mode (single row) --- */}
                    <div
                      className="details-line4567 details-line-24567"
                      style={{
                        marginTop: 12,
                        display: "flex",
                        gap: 35,
                        alignItems: "flex-start",
                      }}
                    >
                      {/* Title (flexible) */}
                      <div
                        className="cp-field-title"
                        style={{ flex: "0 0 300px  " }}
                      >
                        <InputField
                          id="taskTitle"
                          ref={titleRef}
                          label="Stage Title"
                          value={values.taskTitle}
                          onChange={(e) =>
                            setFieldValue("taskTitle", e.target.value)
                          }
                          placeholder="Enter stage title"
                        />
                        {touched.taskTitle && errors.taskTitle && (
                          <div className="field-error4567">
                            {errors.taskTitle}
                          </div>
                        )}
                      </div>

                      {/* Priority (fixed width) */}
                      <div
                        className="cp-field-priority"
                        style={{ marginRight: 15, marginTop: "5px" }}
                      >
                        <label
                          style={{
                            display: "block",
                            fontSize: 13,
                            marginBottom: 4,
                          }}
                        >
                          Priority
                        </label>
                        <div
                          style={{ display: "flex", gap: 8, flexWrap: "wrap" }}
                        >
                          {["LOW", "MEDIUM", "HIGH"].map((p) => (
                            <button
                              key={p}
                              type="button"
                              className={`createtaskprioritybadge ${p.toLowerCase()} ${
                                values.priority === p ? "selected" : ""
                              }`}
                              style={{
                                padding: "6px 14px",
                                border: "none",
                                borderRadius: 999,
                                cursor: "pointer",
                                fontSize: 13,
                              }}
                              onClick={() => setFieldValue("priority", p)}
                            >
                              {p.charAt(0) + p.slice(1).toLowerCase()}
                            </button>
                          ))}
                        </div>
                      </div>

                      {/* Scheduling Mode (fixed width) */}
                      <div
                        className="cp-field-scheduling"
                        style={{
                          width: 160,
                          marginRight: 15,
                          bottom: "10px",
                          marginTop: "-5px",
                        }}
                      >
                        <label
                          style={{
                            display: "block",
                            fontSize: 13,
                            marginBottom: 6,
                          }}
                        >
                          Scheduling Mode
                        </label>

                        <div style={{ display: "flex", gap: 8 }}>
                          {[
                            { val: "DATES", label: "By Dates" },
                            { val: "DAYS", label: "By Duration" },
                          ].map((opt) => (
                            <button
                              key={opt.val}
                              type="button"
                              onClick={() => {
                                const val = opt.val;
                                // mirror the logic you had in AnimatedDropdown.onSelect
                                setFieldValue("schedulingMode", val);

                                const todayDate = new Date();
                                const format = (d) =>
                                  d.toISOString().split("T")[0];

                                if (val === "DATES") {
                                  // when switching to DATES mode — start = today, end = next day
                                  const nextDay = new Date(todayDate);
                                  nextDay.setDate(todayDate.getDate() + 1);

                                  setFieldValue("startDate", format(todayDate));
                                  setFieldValue("endDate", format(nextDay));
                                  setFieldValue("durationDays", "");
                                } else {
                                  // DAYS mode — always ensure startDate = today
                                  setFieldValue("startDate", format(todayDate));
                                  setFieldValue("endDate", "");
                                  if (!values.durationDays)
                                    setFieldValue("durationDays", 2);
                                }
                              }}
                              className={`scheduling-pill ${
                                values.schedulingMode === opt.val
                                  ? "selected"
                                  : ""
                              }`}
                              aria-pressed={values.schedulingMode === opt.val}
                              style={{
                                flex: 1,
                                padding: "6px 8px",
                                borderRadius: 999,
                                border: "1px solid #d6dce0",
                                background:
                                  values.schedulingMode === opt.val
                                    ? "#0b76ff"
                                    : "#ffffff",
                                color:
                                  values.schedulingMode === opt.val
                                    ? "#fff"
                                    : "#222",
                                cursor: "pointer",
                                fontSize: 13,
                              }}
                            >
                              {opt.label}
                            </button>
                          ))}
                        </div>
                      </div>
                    </div>

                    {/* --- Line 3: Description (left) + Start/End stacked or Duration (right) --- */}
                    <div
                      className="details-line4567 details-line-34567"
                      style={{
                        marginTop: 12,
                        display: "flex",
                        gap: 83,
                        alignItems: "flex-start",
                      }}
                    >
                      {/* Description - left column (flex) */}
                      <div
                        className="cp-field-description"
                        style={{ flex: "0 0 500px", marginLeft: 4 }}
                      >
                        <InputField
                          id="description"
                          label="Description"
                          as="textarea"
                          rows={6} // <- add this if InputField forwards rows to textarea
                          style={{ minHeight: 130 }}
                          value={values.description}
                          onChange={(e) =>
                            setFieldValue("description", e.target.value)
                          }
                          placeholder="Optional description"
                        />
                        {/* optional description error won't normally exist, kept for parity */}
                        {touched.description && errors.description && (
                          <div className="field-error4567">
                            {errors.description}
                          </div>
                        )}

                        <div style={{ marginTop: 12 }}>
                          <InputField
                            id="remark"
                            label="Remark"
                            as="textarea"
                            rows={3}
                            style={{ minHeight: 70 }}
                            value={values.remark}
                            onChange={(e) =>
                              setFieldValue("remark", e.target.value)
                            }
                            placeholder="Optional remark"
                          />
                          {touched.remark && errors.remark && (
                            <div className="field-error4567">
                              {errors.remark}
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Date/duration - right column (stacked) */}
                      <div
                        className="cp-field-daterange"
                        style={{
                          width: 220,
                          display: "flex",
                          flexDirection: "column",
                          gap: 12,
                        }}
                      >
                        {values.schedulingMode === "DATES" ? (
                          <>
                            <div>
                              <InputField
                                id="startDate"
                                label={`Start Date `}
                                type="date"
                                value={values.startDate}
                                onChange={(e) =>
                                  setFieldValue("startDate", e.target.value)
                                }
                              />
                              {touched.startDate && errors.startDate && (
                                <div className="field-error4567">
                                  {errors.startDate}
                                </div>
                              )}
                            </div>

                            <div>
                              <InputField
                                id="endDate"
                                label="End Date"
                                type="date"
                                value={values.endDate}
                                onChange={(e) =>
                                  setFieldValue("endDate", e.target.value)
                                }
                              />
                              {touched.endDate && errors.endDate && (
                                <div className="field-error4567">
                                  {errors.endDate}
                                </div>
                              )}
                            </div>
                          </>
                        ) : (
                          // schedulingMode === "DAYS" -> show only duration
                          <div>
                            <InputField
                              id="durationDays"
                              label="Duration (days)"
                              type="number"
                              min={1}
                              value={values.durationDays}
                              onChange={(e) =>
                                setFieldValue("durationDays", e.target.value)
                              }
                            />
                            {touched.durationDays && errors.durationDays && (
                              <div className="field-error4567">
                                {errors.durationDays}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  </section>

                  {/* Todos */}
                  <section
                    className={`section4567 ${
                      activeStep === "todos" ? "active-section4567" : ""
                    }`}
                    aria-hidden={activeStep !== "todos"}
                  >
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "flex-start",
                        marginBottom: 12,
                        gap: 12,
                      }}
                    >
                      <h3>Tasks</h3>
                      <AddButton
                        onClick={() => addTodo()}
                        title="Add ToDo"
                        label=""
                        size={30}
                        textPosition="right"
                        text="Add New Task"
                      />
                    </div>

                    <div className="todos-wrap4567">
                      {todos.length === 0 ? (
                        <div style={{ color: "#666", padding: 12 }}>
                          No task yet. Click + to add one.
                        </div>
                      ) : (
                        todos.map((t, ti) => {
                          const isSelected = selectedTodoId === t.id;
                          // Effective assignee for UI (matches payload fallback logic)
                          const displayedTodoAssignedEmpId =
                            t.assignedEmpId !== null &&
                            t.assignedEmpId !== undefined
                              ? Number(t.assignedEmpId)
                              : Number(loggedInEmpId ?? null);

                          const assignedMember = getMemberByEmpId(
                            displayedTodoAssignedEmpId,
                          );

                          // somewhere above or outside the map — this is wrong

                          return (
                            <div
                              key={t.id}
                              className={`todo-card4567 ${
                                isSelected ? "todo-selected4567" : ""
                              } todo-animate`}
                            >
                              {/* Delete ToDo button (top-right corner) */}
                              <div
                                style={{
                                  position: "absolute",
                                  top: "8px",
                                  right: "8px",
                                  zIndex: 10, // ensure it stays above other elements
                                }}
                              >
                                <DeleteButton
                                  // size={40}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    removeTodo(t.id);
                                  }}
                                />
                              </div>

                              {/* --- LINE 1: Title + Priority --- */}
                              <div
                                className="todo-line1-4567"
                                style={{
                                  display: "flex",
                                  alignItems: "flex-start",
                                  gap: 20,
                                }}
                              >
                                {/* Title (flex 1) */}
                                <div
                                  className="cp-field-title"
                                  style={{ flex: "0 0 500px" }}
                                >
                                  <InputField
                                    id={`todo-${t.id}-title`}
                                    label="Title"
                                    value={t.toDoTitle}
                                    // inside the InputField for title (replace the onKeyDown)
                                    onKeyDown={(e) => {
                                      if (e.key === "Enter") {
                                        e.preventDefault();
                                        const newId = addTodo();
                                        // immediately try to focus the new card's title input after DOM updates
                                        setTimeout(() => {
                                          const newInput =
                                            document.getElementById(
                                              `todo-${newId}-title`,
                                            );
                                          if (
                                            newInput &&
                                            typeof newInput.focus === "function"
                                          ) {
                                            newInput.focus();
                                            try {
                                              newInput.select &&
                                                newInput.select();
                                            } catch (err) {}
                                          }
                                        }, 60);
                                      }
                                    }}
                                    onChange={(e) =>
                                      updateTodo(
                                        t.id,
                                        "toDoTitle",
                                        e.target.value,
                                      )
                                    }
                                    placeholder="Enter task title"
                                    ref={ti === 0 ? todoTitleRef : null}
                                  />
                                </div>

                                {/* Priority (fixed width) */}
                                <div
                                  className="cp-field-priority"
                                  style={{ marginRight: 15, marginTop: "20px" }}
                                >
                                  <label
                                    style={{
                                      display: "block",
                                      fontSize: 13,
                                      marginBottom: 4,
                                    }}
                                  >
                                    Priority
                                  </label>
                                  <div
                                    style={{
                                      display: "flex",
                                      gap: 8,
                                      flexWrap: "wrap",
                                    }}
                                  >
                                    {["LOW", "MEDIUM", "HIGH"].map((p) => (
                                      <button
                                        key={p}
                                        type="button"
                                        className={`createtaskprioritybadge ${p.toLowerCase()} ${
                                          values.priority === p
                                            ? "selected"
                                            : ""
                                        }`}
                                        style={{
                                          padding: "6px 14px",
                                          border: "none",
                                          borderRadius: 999,
                                          cursor: "pointer",
                                          fontSize: 13,
                                        }}
                                        onClick={() =>
                                          setFieldValue("priority", p)
                                        }
                                      >
                                        {p.charAt(0) + p.slice(1).toLowerCase()}
                                      </button>
                                    ))}
                                  </div>
                                </div>
                              </div>

                              {/* --- LINE 2: Remark + Avatar + Assign button --- */}
                              <div
                                className="todo-line2-4567"
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 20,
                                  marginTop: 10,
                                }}
                              >
                                {/* Remark (flex 1, grows full width if alone) */}
                                <div
                                  className="cp-field-remark"
                                  style={{ flex: 1 }}
                                >
                                  <InputField
                                    id={`todo-${t.id}-remark`}
                                    label="Remark"
                                    as="textarea"
                                    value={t.remark}
                                    onChange={(e) =>
                                      updateTodo(t.id, "remark", e.target.value)
                                    }
                                    placeholder="Optional remark"
                                  />
                                </div>

                                {/* Avatar chip */}
                                <div
                                  className="todo-avatar4567"
                                  style={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 8,
                                    background: "#f5f7fb",
                                    padding: "6px 10px",
                                    borderRadius: 20,
                                    minWidth: 130,
                                  }}
                                >
                                  {assignedMember ? (
                                    <>
                                      <div
                                        style={{
                                          width: 28,
                                          height: 28,
                                          borderRadius: "50%",
                                          background: "#e6eefc",
                                          display: "flex",
                                          alignItems: "center",
                                          justifyContent: "center",
                                          fontWeight: 700,
                                        }}
                                      >
                                        {assignedMember.empName
                                          .split(" ")
                                          .map((s) => s[0])
                                          .slice(0, 2)
                                          .join("")
                                          .toUpperCase()}
                                      </div>
                                      <div style={{ fontSize: 13 }}>
                                        {assignedMember.empName ||
                                          (displayedTodoAssignedEmpId ===
                                          Number(loggedInEmpId)
                                            ? "You"
                                            : `#${displayedTodoAssignedEmpId}`)}
                                      </div>
                                    </>
                                  ) : (
                                    <div
                                      style={{ color: "#999", fontSize: 12 }}
                                    >
                                      Unassigned
                                    </div>
                                  )}
                                </div>

                                {/* Assign Button */}
                                <div
                                  className="cp-field-assignbtn"
                                  style={{ width: 160 }}
                                >
                                  {canAssignTodo(currentUserRole) ? (
                                    <ButtonWithIcon
                                      text="Assign"
                                      icon="mdi:account-plus"
                                      type="button"
                                      iconPosition="right"
                                      width={140}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        openAssignModal({
                                          mode: "todo",
                                          todoId: t.id,
                                        });
                                      }}
                                    />
                                  ) : (
                                    <div
                                      style={{ color: "#999", fontSize: 12 }}
                                    >
                                      Auto-assigned
                                    </div>
                                  )}
                                </div>
                              </div>

                              {/* --- Checklist (unchanged) --- */}
                              <div style={{ marginTop: 12 }}>
                                <strong>Todo/Checklist</strong>
                                <div style={{ marginTop: 8 }}>
                                  {(t.toDoList || []).length === 0 ? (
                                    <div style={{ color: "#666" }}>
                                      No Todo/checklist items. Add one below.
                                    </div>
                                  ) : (
                                    (t.toDoList || []).map((li) => (
                                      <div
                                        key={li.id}
                                        style={{
                                          display: "flex",
                                          gap: 8,
                                          alignItems: "center",
                                          marginTop: 6,
                                        }}
                                      >
                                        <input
                                          value={li.toDoListTitle}
                                          onChange={(e) =>
                                            setTodos((prev) =>
                                              prev.map((tt) =>
                                                tt.id === t.id
                                                  ? {
                                                      ...tt,
                                                      toDoList: tt.toDoList.map(
                                                        (x) =>
                                                          x.id === li.id
                                                            ? {
                                                                ...x,
                                                                toDoListTitle:
                                                                  e.target
                                                                    .value,
                                                              }
                                                            : x,
                                                      ),
                                                    }
                                                  : tt,
                                              ),
                                            )
                                          }
                                          placeholder="Todo/Checklist item title"
                                          style={{
                                            flex: 1,
                                            padding: "6px 8px",
                                            borderRadius: 6,
                                            border: "1px solid #ddd",
                                          }}
                                        />
                                        <DeleteButton
                                          size={35}
                                          onClick={(e) => {
                                            e.stopPropagation(); // prevent triggering parent click
                                            removeTodoListItem(t.id, li.id); // your existing handler
                                          }}
                                        />
                                      </div>
                                    ))
                                  )}
                                  <div
                                    style={{
                                      marginTop: 8,
                                      display: "flex",
                                      gap: 8,
                                    }}
                                  >
                                    <AddButton
                                      onClick={() => addTodoListItem(t.id, "")}
                                      tooltip="Add checklist item"
                                      text="Add Todo/checklist item"
                                      textPosition="right"
                                      size={30}
                                    />
                                  </div>
                                </div>
                              </div>
                            </div>
                          );
                        })
                      )}
                    </div>

                    {/* Unified AssignModal render */}
                    {typeof document !== "undefined" &&
                      createPortal(
                        <AssignModal
                          visible={!!assigningTarget}
                          target={assigningTarget}
                          onClose={() => setAssigningTarget(null)}
                        />,
                        document.body,
                      )}
                  </section>

                  {/* Footer */}
                  <div className="cp-footer4567">
                    {/* {activeStep !== "todos" && (
                    <ButtonWithIcon
                      text="Cancel"
                      icon="mdi:close"
                      type="button"
                      iconPosition="right"
                      width={150}
                      onClick={() => onClose && onClose()}
                    />
                  )} */}

                    {activeStep === "todos" && (
                      <>
                        <ButtonWithIcon
                          text="Back to Details"
                          icon="mdi:arrow-left"
                          type="button"
                          color="warning"
                          width={200}
                          onClick={() => setActiveStep("details")}
                        />
                        <ButtonWithIcon
                          text={
                            isSubmitting || submitting
                              ? "Creating..."
                              : "Create Stage"
                          }
                          icon="mdi:check-circle"
                          type="submit"
                          color="success"
                          iconPosition="right"
                          width={180}
                          disabled={isSubmitting || submitting}
                        />
                      </>
                    )}
                    {activeStep === "details" && (
                      <>
                        {/* Create Task directly from Details */}
                        <ButtonWithIcon
                          text={
                            isSubmitting || submitting
                              ? "Creating..."
                              : "Create Stage"
                          }
                          color="success"
                          icon="mdi:check-circle"
                          type="submit"
                          iconPosition="right"
                          width={180}
                          disabled={isSubmitting || submitting}
                        />

                        {/* Keep navigation to Todos */}
                        <ButtonWithIcon
                          text="Go to Todos"
                          icon="mdi:arrow-right"
                          type="button"
                          width={170}
                          iconPosition="right"
                          onClick={() => setActiveStep("todos")}
                        />
                      </>
                    )}
                  </div>

                  {error && <div className="alert4567 error4567">{error}</div>}
                  {message && (
                    <div className="alert4567 success4567">{message}</div>
                  )}
                  {/* <pre>{JSON.stringify(values, null, 2)}</pre> */}
                </form>
              )}
            </Formik>
          </div>
        </main>
      </div>
    </div>
  );
}

CreateTaskModal.propTypes = {
  projectId: PropTypes.oneOfType([PropTypes.number, PropTypes.string])
    .isRequired,
  onClose: PropTypes.func,
  onCreated: PropTypes.func,
  creatorEmpId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  currentEmpId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
};

// --- inline styles for assign modal (move to SCSS if you prefer) ---
const assignModalOverlayStyle = {
  position: "fixed",
  left: 0,
  top: 0,
  right: 0,
  bottom: 0,
  background: "rgba(0,0,0,0.35)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  zIndex: 1200,
};
const assignModalStyle = {
  width: 720,
  maxWidth: "calc(100% - 32px)",
  background: "#fff",
  borderRadius: 8,
  padding: 16,
  boxShadow: "0 12px 30px rgba(0,0,0,0.12)",
};
const closeBtnStyle = {
  border: "none",
  background: "transparent",
  fontSize: 20,
  cursor: "pointer",
};
